import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Home,
  Users,
  Upload,
  LogOut,
  Menu,
  X,
  Settings,
  UserCheck,
  Download,
  Shield,
  Bell,
  Calendar,
  UserPlus,
  FileSpreadsheet,
  Award,
  Lock,
  TrendingUp,
  Activity,
  UtensilsCrossed,
  BookOpen,
  Camera,
  FolderPlus,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  PhoneCall,
} from "lucide-react";
import useHostelSettings from "@/hooks/useHostelSettings";

// Import admin sub-pages
import AdminHome from "@/components/admin/AdminHome";
import StudentManagementAdmin from "@/components/admin/StudentManagementAdmin";
import StudentEdit from "@/components/admin/StudentEdit";
import StudentAssignment from "@/components/admin/StudentAssignment";
import BulkImport from "@/components/admin/BulkImport";
import AdminSettings from "@/components/admin/AdminSettings";
import WardenAssignment from "@/components/admin/WardenAssignment";
import WardenPermissions from "@/components/admin/WardenPermissions";
import DataExport from "@/components/admin/DataExport";
import WardenManagement from "@/components/admin/WardenManagement";
import WardenStudentAssignment from "@/components/admin/WardenStudentAssignment";
import StudentProfile from "@/components/shared/StudentProfile";
import AnnouncementBoard from "@/components/admin/AnnouncementBoard";
import HolidayManagement from "@/components/admin/HolidayManagement";
import ParentManagement from "@/components/admin/ParentManagement";
import StudentPromotion from "@/components/admin/StudentPromotion";
import ClassManagement from "@/components/admin/ClassManagement";
import BulkUpload from "@/components/admin/BulkUpload";
import ResultsManagement from "@/components/admin/ResultsManagement";
import ActivityLog from "@/components/admin/ActivityLog";
import NutritionManagement from "@/components/admin/NutritionManagement";
import AcademicManagement from "@/components/admin/AcademicManagement";
import AdminGallery from "@/components/admin/AdminGallery";
import WardenLoggedIssues from "@/components/admin/WardenLoggedIssues";
import AllParentsRequests from "@/components/admin/AllParentsRequests";
import { Helmet } from "react-helmet";
import CommonPasswordForgotModal from "@/components/common/CommonPasswordForgotModal";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { settings, logoUrl, shortAddress } = useHostelSettings();

  const isMoreActive = [
    "/admin/activity-log",
    "/admin/warden-issues",
    "/admin/parent-requests",
  ].includes(location.pathname);

  const [moreOpen, setMoreOpen] = useState(isMoreActive);

  useEffect(() => {
    if (isMoreActive) {
      setMoreOpen(true);
    }
  }, [location.pathname]);

  // Update favicon when logo changes
  useEffect(() => {
    if (logoUrl) {
      let favicon = document.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = logoUrl;
    }
  }, [logoUrl]);

  const menuItems = [
    { icon: Home, label: "Dashboard", path: "/admin", testId: "nav-dashboard" },
    {
      icon: Users,
      label: "Students",
      path: "/admin/students",
      testId: "nav-students",
    },
    {
      icon: FileSpreadsheet,
      label: "Bulk Upload",
      path: "/admin/bulk-upload",
      testId: "nav-bulk-upload",
    },
    {
      icon: Award,
      label: "Results Management",
      path: "/admin/results",
      testId: "nav-results",
    },
    {
      icon: TrendingUp,
      label: "Student Promotion",
      path: "/admin/promotions",
      testId: "nav-promotions",
    },
    {
      icon: UserPlus,
      label: "Parents",
      path: "/admin/parents",
      testId: "nav-parents",
    },
    {
      icon: Upload,
      label: "Classes",
      path: "/admin/classes",
      testId: "nav-classes",
    },
    {
      icon: BookOpen,
      label: "Academic",
      path: "/admin/academic",
      testId: "nav-academic",
    },
    {
      icon: Shield,
      label: "Wardens",
      path: "/admin/wardens",
      testId: "nav-wardens",
    },
    {
      icon: Lock,
      label: "Assign Rights to Wardens",
      path: "/admin/warden-permissions",
      testId: "nav-warden-permissions",
    },
    {
      icon: Bell,
      label: "Notice Board",
      path: "/admin/announcements",
      testId: "nav-announcements",
    },
    {
      icon: UtensilsCrossed,
      label: "Nutrition",
      path: "/admin/nutrition",
      testId: "nav-nutrition",
    },
    {
      icon: Calendar,
      label: "Holidays",
      path: "/admin/holidays",
      testId: "nav-holidays",
    },
    {
      icon: Download,
      label: "Report Generations",
      path: "/admin/export",
      testId: "nav-export",
    },
    {
      icon: Camera,
      label: "School Gallery",
      path: "/admin/gallery",
      testId: "nav-gallery",
    },
    {
      isMoreMenu: true,
      icon: FolderPlus,
      label: "More",
      testId: "nav-more",
      subItems: [
        {
          icon: Activity,
          label: "Activity Log",
          path: "/admin/activity-log",
          testId: "nav-activity-log",
        },
        {
          icon: ShieldAlert,
          label: "Warden Logged Issues",
          path: "/admin/warden-issues",
          testId: "nav-warden-issues",
        },
        {
          icon: PhoneCall,
          label: "All Parents Request",
          path: "/admin/parent-requests",
          testId: "nav-parent-requests",
        },
      ],
    },
    {
      icon: Settings,
      label: "Settings",
      path: "/admin/settings",
      testId: "nav-settings",
    },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const activeHeaderItem = menuItems
    .flatMap((item) => (item.subItems ? item.subItems : [item]))
    .find((item) => item.path === location.pathname);

  return (
    <>
      <Helmet>
        <title>{process.env.REACT_APP_NAME} || ADMIN</title>
        <link rel="icon" href="/favicon.webp" type="image/webp" />
      </Helmet>
      <div className="flex h-screen" style={{ background: "#F8FBFD" }}>
        {/* Sidebar */}
        <div
          className={`fixed lg:static inset-y-0 left-0 z-50 w-70 transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
          style={{
            background: "linear-gradient(to bottom, #5A9FD4 0%, #4A8BC2 100%)",
          }}
        >
          <div className="flex flex-col h-full text-white">
            {/* Logo & Hostel Info */}
            <div className="p-6 border-b border-white/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-white/20 rounded flex items-center justify-center">
                      <Home className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h1 className="text-xl font-bold leading-tight">
                      {settings.name || "DormCircle"}
                    </h1>
                    {shortAddress && (
                      <p className="text-xs opacity-90">{shortAddress}</p>
                    )}
                  </div>
                </div>
                <button
                  data-testid="close-sidebar-btn"
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm opacity-90">Admin Portal</p>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 overflow-y-auto p-4">
              <ul className="space-y-2">
                {menuItems.map((item) => {
                  if (item.isMoreMenu) {
                    return (
                      <li key="more-menu">
                        <button
                          data-testid={item.testId}
                          onClick={() => setMoreOpen(!moreOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                            isMoreActive
                              ? "bg-white/20 shadow-md font-semibold"
                              : "hover:bg-white/10"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                          </div>
                          {moreOpen ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>

                        {moreOpen && (
                          <ul className="mt-1 space-y-1 pl-4 border-l-2 border-white/30 ml-4">
                            {item.subItems.map((sub) => (
                              <li key={sub.path}>
                                <button
                                  data-testid={sub.testId}
                                  onClick={() => {
                                    navigate(sub.path);
                                    setSidebarOpen(false);
                                  }}
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                                    location.pathname === sub.path
                                      ? "bg-white/30 font-bold shadow-sm text-white"
                                      : "hover:bg-white/10 text-white/90"
                                  }`}
                                >
                                  <sub.icon className="w-4 h-4" />
                                  <span>{sub.label}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  }

                  return (
                    <li key={item.path}>
                      <button
                        data-testid={item.testId}
                        onClick={() => {
                          navigate(item.path);
                          setSidebarOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                          location.pathname === item.path
                            ? "bg-white/20 shadow-md"
                            : "hover:bg-white/10"
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* User Info & Logout */}
            <div className="p-4 border-t border-white/20">
              <div className="mb-4 p-3 bg-white/10 rounded-lg">
                <p className="font-semibold">{user?.name}</p>
                <p className="text-sm opacity-90">{user?.phone}</p>
                <p className="text-xs opacity-75 mt-1">Administrator</p>
              </div>
              <div className="my-5">
                <CommonPasswordForgotModal role={"admin"} />
              </div>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="w-full border-white/30 text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white shadow-sm p-4 flex items-center justify-between">
            <button
              data-testid="open-sidebar-btn"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" style={{ color: "#2C5F7F" }} />
            </button>
            <h2 className="text-xl font-semibold" style={{ color: "#2C5F7F" }}>
              {activeHeaderItem?.label || "Dashboard"}
            </h2>
            <div className="w-10"></div>
          </header>

          {/* Content Area */}
          <main className="flex-1 overflow-y-auto p-6">
            <Routes>
              <Route index element={<AdminHome />} />
              <Route path="students" element={<StudentManagementAdmin />} />
              <Route
                path="students/edit/:studentId"
                element={<StudentEdit />}
              />
              <Route
                path="students/:studentId"
                element={<StudentProfile userRole="admin" />}
              />
              <Route
                path="student-assignment"
                element={<StudentAssignment />}
              />
              <Route path="bulk-upload" element={<BulkUpload />} />
              <Route path="results" element={<ResultsManagement />} />
              <Route path="promotions" element={<StudentPromotion />} />
              <Route path="parents" element={<ParentManagement />} />
              <Route path="classes" element={<ClassManagement />} />
              <Route path="wardens" element={<WardenManagement />} />
              <Route
                path="wardens/assign/:wardenId"
                element={<WardenStudentAssignment />}
              />
              <Route path="warden-assignment" element={<WardenAssignment />} />
              <Route
                path="warden-permissions"
                element={<WardenPermissions />}
              />
              <Route path="activity-log" element={<ActivityLog />} />
              <Route path="warden-issues" element={<WardenLoggedIssues />} />
              <Route path="parent-requests" element={<AllParentsRequests />} />
              <Route path="announcements" element={<AnnouncementBoard />} />
              <Route path="nutrition" element={<NutritionManagement />} />
              <Route path="academic" element={<AcademicManagement />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="holidays" element={<HolidayManagement />} />
              <Route path="export" element={<DataExport />} />
              <Route path="settings" element={<AdminSettings />} />
              {/* Legacy route support */}
              <Route path="bulk-import" element={<BulkImport />} />
              <Route path="warden-management" element={<WardenManagement />} />
            </Routes>
          </main>
        </div>

        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
      </div>
    </>
  );
};

export default AdminDashboard;
